import React from 'react';
import { Check, PenLine, ScanSearch } from 'lucide-react';
import { formatScore, scoreBand, severityBand } from '../lib/damage';

const BAND_TEXT = { high: 'text-sev-high', med: 'text-sev-med', low: 'text-sev-low' };
const BAND_CHIP = {
  high: 'bg-sev-high-soft text-sev-high',
  med: 'bg-sev-med-soft text-sev-med',
  low: 'bg-sev-low-soft text-sev-low',
};

// Severity is triple-coded (colour, shape, word) so it reads without colour.
function BandShape({ band }) {
  if (band === 'high') return <svg viewBox="0 0 10 10" className="h-2.5 w-2.5 shrink-0" aria-hidden="true"><path d="M5 0 10 10H0Z" fill="currentColor" /></svg>;
  if (band === 'med') return <svg viewBox="0 0 10 10" className="h-2.5 w-2.5 shrink-0" aria-hidden="true"><path d="M5 0 10 5 5 10 0 5Z" fill="currentColor" /></svg>;
  return <svg viewBox="0 0 10 10" className="h-2.5 w-2.5 shrink-0" aria-hidden="true"><circle cx="5" cy="5" r="5" fill="currentColor" /></svg>;
}

const chipBase = 'inline-flex h-6 items-center gap-1 whitespace-nowrap rounded-sm px-2 text-[13px] font-bold leading-4';

export function SeverityChip({ severity, score, className = '' }) {
  const band = score !== undefined ? scoreBand(score) : severityBand(severity);
  if (!band) return null;
  return (
    <span className={`${chipBase} ${BAND_CHIP[band.key]} ${className}`}>
      <BandShape band={band.key} />
      {band.label}
    </span>
  );
}

const PROVENANCE = {
  ai: { label: 'AI detected', Icon: ScanSearch, cls: 'bg-prov-ai-soft text-prov-ai' },
  ai_edited: { label: 'AI detected, edited', Icon: ScanSearch, cls: 'bg-prov-ai-soft text-prov-ai' },
  manual: { label: 'Manual entry', Icon: PenLine, cls: 'bg-marker-soft text-ink dark:text-marker' },
};

export function ProvenanceChip({ source = 'ai', className = '' }) {
  const p = PROVENANCE[source] || PROVENANCE.ai;
  const { Icon } = p;
  return (
    <span className={`${chipBase} ${p.cls} ${className}`}>
      <Icon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
      {p.label}
    </span>
  );
}

const STATUS_TONE = {
  review: 'bg-marker-soft text-ink',
  neutral: 'bg-sunken text-ink-muted',
};

export function StatusChip({ tone = 'neutral', done = false, children, className = '' }) {
  return (
    <span className={`${chipBase} ${STATUS_TONE[tone] || STATUS_TONE.neutral} ${className}`}>
      {done && <Check className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />}
      {children}
    </span>
  );
}

/** Numbered evidence-tent marker that ties a photo region to a finding row. */
export function EvidenceMarker({ n, size = 'md', className = '' }) {
  const dims = {
    sm: 'h-[22px] w-6 pb-0.5 text-xs',
    md: 'h-[26px] w-7 pb-[3px] text-sm',
    lg: 'h-[34px] w-9 pb-[5px] text-lg',
  }[size];
  return (
    <span
      className={`inline-flex shrink-0 items-end justify-center bg-marker font-mono font-bold leading-none text-on-marker [clip-path:polygon(20%_0,80%_0,100%_100%,0_100%)] ${dims} ${className}`}
    >
      {n}
    </span>
  );
}

/** Thin 0 to 100 bar split at the band thresholds, with a tick at the score. */
export function ScoreBar({ score, className = '' }) {
  const pct = score === null || score === undefined ? null : Math.max(0, Math.min(100, Number(score)));
  return (
    <span aria-hidden="true" className={`relative grid h-2 grid-cols-[40fr_20fr_40fr] gap-0.5 ${className}`}>
      <span className="border border-sev-low bg-sev-low-soft" />
      <span className="border border-sev-med bg-sev-med-soft" />
      <span className="border border-sev-high bg-sev-high-soft" />
      {pct !== null && <i className="absolute -top-[5px] -ml-px h-[18px] w-[3px] bg-ink" style={{ left: `${pct}%` }} />}
    </span>
  );
}

/** A damage score read as a measurement: number, scale, band. */
export function DamageScore({ score, size = 'lg', showBar = true, className = '' }) {
  const band = scoreBand(score);
  const numCls = size === 'lg' ? 'text-[56px] leading-[56px]' : 'text-[26px] leading-8';
  return (
    <div className={`inline-grid justify-items-start gap-2 ${className}`}>
      <p className="m-0 flex items-baseline gap-2 font-mono tabular">
        <span className={`font-bold ${numCls} ${band ? BAND_TEXT[band.key] : 'text-ink-muted'}`}>{formatScore(score)}</span>
        <span className="text-[15px] font-semibold text-ink-muted">/100</span>
      </p>
      {showBar && <ScoreBar score={score} className={size === 'lg' ? 'w-[200px]' : 'w-full max-w-[160px]'} />}
      {size === 'lg' && <span className="text-sm font-bold">{band ? band.long : 'Not scored yet'}</span>}
    </div>
  );
}

/** Inline score: bold number in its band colour, for tables and lists. */
export function ScoreNumber({ score, className = '' }) {
  const band = scoreBand(score);
  return (
    <span className={`font-mono text-[17px] font-bold tabular ${band ? BAND_TEXT[band.key] : 'text-ink-muted'} ${className}`}>
      {formatScore(score)}
    </span>
  );
}
